import React from 'react'
import { Box, Button, Typography, CircularProgress, Alert, Select, MenuItem, FormControl } from '@mui/material'
import { ReactComponent as ShieldIcon } from '../../../../../../../common/icons/shield_check_icon.svg'
import MailTwoToneIcon from '@mui/icons-material/MailTwoTone'

export default function EmailStep({
  domainName = 'yourdomain.com',
  selectedEmail = 'admin@yourdomain.com',
  onSelectedEmailChange,
  availableEmails = [],
  onInitiateResend,
  resendCooldownActive = false,
  resendSecondsLeft = 0,
  isLoading = false,
  error,
  showSuccessNotification = false,
  isInitialSend = true
}) {
  const handleResendClick = () => {
    if (onInitiateResend) {
      onInitiateResend()
    }
  }

  const handleEmailChange = event => {
    if (onSelectedEmailChange) {
      onSelectedEmailChange(event.target.value)
    }
  }

  const currentSelectedEmail = availableEmails.includes(selectedEmail) ? selectedEmail : availableEmails[0] || ''
  return (
    <Box sx={stepStyles.container}>
      <Alert severity='warning' icon={<ShieldIcon />} sx={stepStyles.warningAlert}>
        <Typography component='div' sx={{ fontWeight: 'bold' }}>
          Verify {domainName}
        </Typography>
        <Typography component='div'>
          For security reasons, it's required that every domain names used on your workspace go through our 2-steps
          validation process to be used.
        </Typography>
        {/*<Typography component='div'>*/}
        {/*  - If you are not comfortable with this method, please{' '}*/}
        {/*  <Link underline={'hover'} onClick={onLinkClicked} disabled={isLoading} sx={stepStyles.inlineLink}>*/}
        {/*    use DNS method*/}
        {/*  </Link>*/}
        {/*  .*/}
        {/*</Typography>*/}
      </Alert>

      {error && (
        <Alert severity='error' sx={{ textAlign: 'left', mb: 2, width: '100%' }}>
          {error}
        </Alert>
      )}

      <Box sx={stepStyles.emailAndActionsContainer}>
        <FormControl fullWidth variant='outlined' sx={stepStyles.emailSelectControl}>
          <Select
            value={currentSelectedEmail}
            onChange={handleEmailChange}
            disabled={isLoading || resendCooldownActive}
            sx={stepStyles.emailSelect}
          >
            {availableEmails.length === 0 && (
              <MenuItem value={currentSelectedEmail} disabled>
                <em>{currentSelectedEmail || 'No emails available'}</em>
              </MenuItem>
            )}
            {availableEmails.map(email => (
              <MenuItem key={email} value={email}>
                {email}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant={isInitialSend && !resendCooldownActive ? 'contained' : 'outlined'}
          onClick={!resendCooldownActive ? handleResendClick : undefined}
          disabled={resendCooldownActive || isLoading || !currentSelectedEmail}
          startIcon={isLoading && !resendCooldownActive ? <CircularProgress size={20} color='inherit' /> : null}
          sx={stepStyles.actionButton}
        >
          {resendCooldownActive
            ? `Resend email in ${resendSecondsLeft} s`
            : isLoading
              ? 'Sending...'
              : isInitialSend
                ? 'Send verification email'
                : 'Resend Email'}
        </Button>
      </Box>

      {/*<Typography sx={stepStyles.dnsLinkText}>*/}
      {/*  If you have access to your DNS provider, you can{' '}*/}
      {/*  <Link onClick={onLinkClicked} disabled={isLoading} underline='hover'>*/}
      {/*    use the DNS method*/}
      {/*  </Link>*/}
      {/*  .*/}
      {/*</Typography>*/}

      {showSuccessNotification && (
        <Alert
          severity='success'
          icon={<MailTwoToneIcon fontSize='inherit' />}
          sx={stepStyles.successAlert}
          variant='standard'
        >
          Your verification email is on the way! Check your inbox (and spam folder) for an email from us.
        </Alert>
      )}
    </Box>
  )
}

const stepStyles = {
  container: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%'
  },
  warningAlert: {
    width: '100%',
    textAlign: 'left',
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: 400,
    fontSize: '16px',
    borderRadius: '16px',
    backgroundColor: '#F7D14A',
    color: '#1B1B26'
  },
  inlineLink: {
    color: '#1B1B26',
    textDecorationColor: '#1B1B26'
  },
  emailAndActionsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    width: '100%',
    mt: 1,
    mb: 1.5,
    flexDirection: { xs: 'column', sm: 'row' }
  },
  emailSelectControl: {
    flexGrow: 1,
    minWidth: 200,
    width: { xs: '100%', sm: 'auto' },
    '& .MuiOutlinedInput-root': {}
  },
  emailSelect: {
    borderRadius: '14px',
    textAlign: 'left',
    fontFamily: 'Plus Jakarta Sans',
    '& .MuiOutlinedInput-root': {
      fontFamily: 'Plus Jakarta Sans',
      height: '60px',
      backgroundColor: '#fff'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      fontFamily: 'Plus Jakarta Sans',
      border: '1px solid #E7E7EE'
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Plus Jakarta Sans',
      fontWeight: 400,
      fontSize: '16px'
    }
  },
  actionButton: {
    borderRadius: '16px',
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: 400,
    fontSize: '16px',
    padding: '8px 8px',
    backgroundColor: '#0246F8',
    boxShadow: 'none',
    color: '#fff',
    textTransform: 'none',
    width: '240px',
    '&:hover': { backgroundColor: '#1d4ed8' },
    '&.Mui-disabled': {
      backgroundColor: '#e0e0e0',
      color: '#757575'
    }
  },
  dnsLinkText: {
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: 400,
    fontSize: '13px',
    textAlign: 'left',
    width: '100%',
    color: '#1B1B26',
    mt: 0.5,
    mb: 2
  },
  successAlert: {
    mt: 2,
    textAlign: 'left',
    fontWeight: 400,
    fontSize: 16,
    fontFamily: 'Plus Jakarta Sans',
    borderRadius: '16px',
    backgroundColor: '#00BC6226',
    color: '#1B1B26',
    border: `1px solid #00BC624D`
  }
}
